import { readChatActorId, readChatSessionIdentity } from 'src/auth/sessionEvents';
// useArchiveChat (issue #419) — PATCH /chats/:chatId/archive.
//
// Per-participant archive (Sprint 2 contract): the server writes
// `ChatParticipants.archivedAt` for the caller only. Optimistically flips
// the caller's `archivedAt` so the row jumps to the right tab without a
// round-trip.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { archiveChat } from './api';
import { chatKeys } from './keys';

import type { Chat } from 'src/models/chat';

interface Args {
  chatId: number;
  archived: boolean;
}

export function useArchiveChat() {
  const queryClient = useQueryClient();
  const sessionIdentity = readChatSessionIdentity();
  return useMutation(
    ({ chatId, archived }: Args) => {
      if (readChatSessionIdentity() !== sessionIdentity) throw new Error('Chat session changed; reopen the conversation.');
      return archiveChat(chatId, { archived });
    },
    {
      onMutate: async ({ chatId, archived }: Args) => {
        if (readChatSessionIdentity() !== sessionIdentity) throw new Error('Chat session changed; reopen the conversation.');
        const root = chatKeys.myChatsRoot();
        await queryClient.cancelQueries(root);
        const queries = queryClient.getQueriesData<Chat[]>(root);
        queries.forEach(([key, value]) => {
          if (!value) return;
          queryClient.setQueryData<Chat[]>(
            key,
            value.map((row) => {
              if (row.id !== chatId) return row;
              const ts = archived ? new Date().toISOString() : null;
              const participants = row.participants?.map((p) =>
                p.userId === readChatActorId() ? { ...p, archivedAt: ts } : p
              );
              return {
                ...row,
                participants,
                isArchived: archived,
              };
            })
          );
        });
      },
      onSettled: () => {
        queryClient.invalidateQueries(chatKeys.myChatsRoot());
      },
    }
  );
}
